import {Color} from '@benchling/nucleus-ui';
import {Accordion} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import DataView from 'analysis-tool/data-view/DataView';
import DataViewContext, {
  DEFAULT_BLOCK_CONFIG,
  OverflowDataViewAction,
} from 'analysis-tool/data-view/DataViewContext';
import {assertAndContinue, assert} from 'Asserts';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import HelpButton from 'help/HelpButton';
import {DEFAULT_BLOCK_HEIGHT} from 'insights/InsightsConstants';
import {FONT_PROXIMA_19, FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import {DRAG_HANDLE_CLASS} from 'ui-library/drag-and-drop/DnDDragHandle';
import type {UseDnDCanvasDragStateOutput} from 'ui-library/drag-and-drop/hooks/useDnDCanvasDragState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import MarkdownContent from 'ui-library/MarkdownContent';
import {assertNever, ElementMouseEvent} from 'util/Types';

import ConfigFormBlock from './ConfigFormBlock';
import {useDataLayoutContext} from './DataLayoutContext';
import {GridBlock} from './DataLayoutTypes';
import type {DraggableBlock} from './GridSectionDnDTypes';

interface GridBlockProps {
  block: GridBlock;
  layoutPath: Array<string | number>;
  hasBorder?: boolean;
  isDraggable?: boolean;
  draggableBlock?: DraggableBlock;
  onDragStart?: UseDnDCanvasDragStateOutput<DraggableBlock>['onItemDragStart'];
}

function BlockContents({block, layoutPath}: GridBlockProps): JSX.Element | null {
  assert(block.type !== 'DATA', 'Data view blocks should be rendered in a DataViewContext.Provider');
  if (block.type === 'CONFIG_FORM') {
    return <ConfigFormBlock block={block} layoutPath={layoutPath} />;
  } else if (block.type === 'MARKDOWN') {
    return <MarkdownContent content={block.markdown} />;
  } else if (block.type === 'PLACEHOLDER') {
    return null;
  } else {
    assertAndContinue(false, 'Found unexpected block type in layout', {
      blockType: assertNever(block).type,
    });
    return null;
  }
}

interface GridBlockHeaderProps {
  title?: string;
  subtitle?: string;
  infoText?: string;
}

export function GridBlockHeaderOrNull({title, subtitle, infoText}: GridBlockHeaderProps): JSX.Element | null {
  if (!title && !subtitle) {
    return null;
  }
  assert(title != null || infoText == null, 'infoText cannot be present without a title');

  return (
    <div>
      {title && (
        <div className={css(gridBlockStyles.gridBlockTitleAndInfoText)}>
          {title && <div className={css(gridBlockStyles.gridBlockTitle)}>{title}</div>}
          {infoText && (
            <HelpButton
              renderContent={() => <span className="u-pre-wrap u-normal-font-weight">{infoText}</span>}
              iconClassName="u-mini-spacing-left"
            />
          )}
        </div>
      )}
      {subtitle && <div className={css(gridBlockStyles.gridBlockSubtitle)}>{subtitle}</div>}
    </div>
  );
}

export function GridBlockBody({
  children,
  isCollapsible = false,
  isInLayout,
}: {
  children: React.ReactNode;
  isCollapsible?: boolean;
  isInLayout?: boolean;
}): JSX.Element {
  if (!isCollapsible && !isInLayout) {
    return <>{children}</>;
  }

  if (!isCollapsible) {
    return <div className={css(isInLayout && gridBlockStyles.gridContentHeight)}>{children}</div>;
  }

  return (
    <Accordion
      classNames={{
        panel: css(gridBlockStyles.accordionPanel, isInLayout && gridBlockStyles.gridContentHeight),
        item: css(gridBlockStyles.accordionItem),
        content: css(gridBlockStyles.accordionContent),
        control: css(gridBlockStyles.accordionControl),
      }}
      variant="filled"
    >
      <Accordion.Item value="block">
        <Accordion.Control>Click to show</Accordion.Control>
        <Accordion.Panel>{children}</Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

function DragHandleIcon({
  draggableBlock,
  onDragStart,
}: {
  draggableBlock: DraggableBlock;
  onDragStart: UseDnDCanvasDragStateOutput<DraggableBlock>['onItemDragStart'];
}): JSX.Element {
  return (
    <div
      className={classNames(DRAG_HANDLE_CLASS, css(gridBlockStyles.dragHandle))}
      onMouseDown={(e: ElementMouseEvent) => {
        onDragStart(e, draggableBlock);
      }}
    >
      <BenchlingIcon icon="drag-handle" className={css(gridBlockStyles.dragHandleIcon)} />
    </div>
  );
}

/** @package */
export default function GridBlock({
  block,
  layoutPath,
  hasBorder,
  isDraggable,
  draggableBlock,
  onDragStart,
}: GridBlockProps): JSX.Element | null {
  const {actionOrigin, isEditable, onEvents, steps} = useDataLayoutContext();

  const dragHandleProps = useMemo(() => {
    if (!isDraggable || !draggableBlock || !onDragStart) {
      return undefined;
    }
    return {
      className: DRAG_HANDLE_CLASS,
      onMouseDown: (e: ElementMouseEvent) => {
        onDragStart(e, draggableBlock);
      },
    };
  }, [isDraggable, draggableBlock, onDragStart]);
  const dataViewContextValue = useMemo(() => {
    if (block.type === 'DATA') {
      const removeAction: OverflowDataViewAction = {
        icon: 'close',
        label: 'Remove view from layout',
        isDisabled: !isEditable,
        helpText: !isEditable ? 'Cannot modify a locked or read-only layout' : undefined,
        onClick: () => {
          onEvents?.([{type: 'REMOVE_BLOCK', layoutPath}]);
        },
      };
      return {
        actionOrigin,
        analysisView: block.view,
        actions: {
          buttonActions: [],
          overflowActions: [removeAction],
        },
        steps,
        blockConfig: block.blockConfig
          ? {...block.blockConfig, isInLayout: true}
          : {...DEFAULT_BLOCK_CONFIG, isInLayout: true},
        dataFrameViewOptions: {
          defaultTableMode: block.tableConfig?.tableMode as DataFrameTableMode | undefined,
        },
        dragHandleProps,
      };
    } else {
      return null;
    }
  }, [actionOrigin, block, dragHandleProps, isEditable, layoutPath, onEvents, steps]);

  if (block.type === 'DATA') {
    assert(dataViewContextValue != null, 'Data view context value should be set for DATA blocks');
    return (
      <DataViewContext.Provider value={dataViewContextValue}>
        <div
          className={css(
            gridBlockStyles.fullHeight,
            hasBorder && gridBlockStyles.blockBorder,
            block.blockConfig?.blockMode === 'CONTENT_ONLY' && gridBlockStyles.contentOnlyBlock
          )}
        >
          <DataView />
        </div>
      </DataViewContext.Provider>
    );
  }

  if (block.type === 'PLACEHOLDER') {
    return <PlaceholderBlockContent layoutPath={layoutPath} />;
  }

  const headerProps =
    block.blockConfig?.blockMode === 'PANE'
      ? {
          title: block.blockConfig?.title ?? undefined,
          subtitle: block.blockConfig?.subtitle ?? undefined,
          infoText: block.blockConfig?.infoText ?? undefined,
        }
      : undefined;

  return (
    <div
      className={css(
        gridBlockStyles.gridBlock,
        gridBlockStyles.fullHeight,
        hasBorder && gridBlockStyles.blockBorder,
        isDraggable && gridBlockStyles.draggableBlockContainer
      )}
    >
      {isDraggable && draggableBlock && onDragStart && (
        <DragHandleIcon draggableBlock={draggableBlock} onDragStart={onDragStart} />
      )}
      <GridBlockHeaderOrNull {...headerProps} />
      <GridBlockBody isCollapsible={block.blockConfig?.isCollapsible ?? undefined}>
        <BlockContents block={block} layoutPath={layoutPath} />
      </GridBlockBody>
    </div>
  );
}

function PlaceholderBlockContent({layoutPath}: {layoutPath: Array<string | number>}): JSX.Element {
  const {isEditable, onEvents} = useDataLayoutContext();
  const isClickable = isEditable === true && onEvents != null;

  const emitAddBlock = (): void => {
    onEvents?.([{type: 'ADD_BLOCK', layoutPath}]);
  };

  const handleClick = isClickable ? emitAddBlock : undefined;
  const handleKeyDown = isClickable
    ? (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          emitAddBlock();
        }
      }
    : undefined;

  return (
    <div
      className={css(gridBlockStyles.emptyBlock, isClickable && gridBlockStyles.placeholderBlockClickable)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={isClickable ? 'button' : undefined}
      aria-label={isClickable ? 'Add view to cell' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <BenchlingIcon icon="plus" className={css(gridBlockStyles.emptyBlockIcon)} />
    </div>
  );
}

export const gridBlockStyles = StyleSheet.create({
  gridBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    padding: 10,
    backgroundColor: Color.WHITE,
    borderRadius: 4,
  },
  blockBorder: {
    border: `1px solid ${Color.GRAY_200}`,
  },
  fullHeight: {
    height: '100%',
  },
  gridBlockTitle: {
    ...FONT_PROXIMA_19,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
  gridBlockTitleAndInfoText: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
  },
  gridBlockSubtitle: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
  accordionContent: {
    height: '100%',
  },
  accordionControl: {
    paddingLeft: 12,
  },
  accordionItem: {
    backgroundColor: Color.WHITE,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  accordionPanel: {
    flexGrow: 1,
  },
  gridContentHeight: {
    // The GridSection sets the max height for the whole block, but we need to set an absolute height
    // for the Chart DATA blocks because we need to pass in an absolute height/width for plotly.
    height: DEFAULT_BLOCK_HEIGHT,
  },
  contentOnlyBlock: {
    backgroundColor: Color.WHITE,
  },
  emptyBlock: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    minHeight: DEFAULT_BLOCK_HEIGHT,
    border: `2px dashed ${Color.GRAY_200}`,
    borderRadius: 4,
  },
  emptyBlockIcon: {
    fontSize: 24,
    color: Color.GRAY_200,
    userSelect: 'none',
  },
  draggableBlockContainer: {
    position: 'relative',
  },
  dragHandle: {
    position: 'absolute',
    top: 4,
    left: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    cursor: 'grab',
    borderRadius: 4,
    zIndex: 1,
    ':hover': {
      backgroundColor: Color.GRAY_100,
    },
  },
  dragHandleIcon: {
    fontSize: 14,
    color: Color.GRAY_400,
    userSelect: 'none',
  },
  placeholderBlockClickable: {
    cursor: 'pointer',
    ':hover': {
      borderColor: Color.GRAY_400,
      backgroundColor: Color.GRAY_50,
    },
    ':focus-visible': {
      borderColor: Color.BLUE_300,
      boxShadow: `0 0 0 2px ${Color.BLUE_100}`,
      outline: 'none',
    },
  },
});
